import React from 'react';

import { defaultOperators } from 'components/Grid/utils';
import GridLookupRenderer from 'components/Grid/renderers/GridLookupRenderer';

import { ColumnDefinition } from 'components/Grid/types';
import { LinksType } from 'utils/api/types';
import { EntityModules, isRegisterEntityModule, ProtectorType } from 'core/constants';
import { EmptyText } from 'components/Grid/renderers/GridListRenderer/styles';
import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { JSONSchemaType } from 'utils/api/modules/incidents/incidents.types';

const entityFilterOperators = (entityModule: EntityModules) =>
  defaultOperators[ProtectorType.Lookup]({
    schema: { properties: { lookupType: entityModule as unknown as JSONSchemaType } },
  });

const getEntityGridColumnForLinks = (
  entityModule: EntityModules,
  disableFiltering = false,
  disableSorting = false
): ColumnDefinition =>
  ({
    defaultVisibility: false,
    field: `links_${entityModule}`,
    nativeFilter: getEntityTranslation[entityModule].singularKey,
    filterable: !disableFiltering,
    sortable: !disableSorting,
    filterOperators: entityFilterOperators(entityModule),
    headerName: getEntityTranslation[entityModule].singular,
    renderCell: ({ value, hasFocus }: { value: Array<LinksType> | null; hasFocus: boolean }) =>
      value && value[0] ? (
        <GridLookupRenderer entity={entityModule} instance={value[0].instance} hasFocus={hasFocus} />
      ) : (
        <EmptyText>N/A</EmptyText>
      ),
    valueGetter: (_value: unknown, row: { links?: Array<LinksType> }) => {
      if (!row.links) return [];
      return _value !== null
        ? row.links.filter((link) => link.entity === entityModule)
        : row.links.find((link) => link.entity === entityModule);
    },
  }) as unknown as ColumnDefinition;

const getEntityGridColumn = (
  entityModule: EntityModules,
  disableFiltering = false,
  disableSorting = false
): ColumnDefinition => {
  const propertyName = getEntityTranslation[entityModule].singularKey;

  return {
    defaultVisibility: false,
    field: entityModule,
    nativeFilter: getEntityTranslation[entityModule].singularKey,
    filterable: !disableFiltering,
    sortable: !disableSorting,
    filterOperators: entityFilterOperators(entityModule),
    headerName: getEntityTranslation[entityModule].singular,
    renderCell: ({ value, hasFocus }: { value: { name: string; uuid: string } | null; hasFocus: boolean }) =>
      value && value.name && value.uuid ? (
        <GridLookupRenderer entity={entityModule} instance={value} hasFocus={hasFocus} />
      ) : (
        <EmptyText>N/A</EmptyText>
      ),
    valueGetter: (_value: unknown, row: Record<string, unknown>) => {
      const entityValue = row[propertyName] as { name: string; uuid: string } | null | undefined;
      return entityValue || null;
    },
  } as unknown as ColumnDefinition;
};

export const getEntityGridColumns = (
  activeEntityModules: EntityModules[],
  disableFiltering = false,
  disableSorting = false,
  links = true
): ColumnDefinition[] =>
  activeEntityModules
    .filter(isRegisterEntityModule)
    .map((module) =>
      links
        ? getEntityGridColumnForLinks(module, disableFiltering, disableSorting)
        : getEntityGridColumn(module, disableFiltering, disableSorting)
    );

export default getEntityGridColumn;
